// src/pages/enterprise/travelChannelForm.js
//
// GOV-TRAVEL-CHANNEL-PANEL-01. The Travel channel tab's pure helpers: the record org-backend
// answers (organization.travelChannel, normalized by org backend
// services/govTravel/travelChannelContract.js) to the form the tab edits, and back.
//
//   toForm(channel)          -> the form (strings for inputs, rows for lists)
//   fromForm(form)           -> the record for PUT, in the normalized shape, so
//                               fromForm(toForm(channel)) equals channel
//   missingSources(form)     -> [{ path, label }]: every outside fact with no source, by the
//                               contract's own Rule 1b list. Save stays off until it is empty.
//   refusalLine(err, voc)    -> one readable line for an org-backend refusal { code, path, detail }
//   privateFarePath(rows, i) -> the contract's path for contract airline row i ('privateFares.UA[0]')
//
// A source is complete only with both its url and its found-on date.

const s = (v) => (v == null ? '' : String(v));
const t = (v) => (typeof v === 'string' && v.trim() ? v.trim() : null);

const sourceToForm = (src) => ({ url: s(src && src.url), foundAt: s(src && src.foundAt) });
const sourceFromForm = (src) => {
    const url = t(src && src.url);
    const foundAt = t(src && src.foundAt);
    return url && foundAt ? { url, foundAt } : null;
};
export const sourceComplete = (src) => !!(t(src && src.url) && t(src && src.foundAt));
const numFromForm = (v) => {
    const x = t(s(v));
    if (x == null) return null;
    const n = Number(x);
    return Number.isFinite(n) ? n : null;
};
const upper = (v) => (t(v) ? t(v).toUpperCase() : null);

export const EMPTY_SOURCE = { url: '', foundAt: '' };
export const newPrivateFare = (airline = '') => ({ airline, id: '', corporateCode: '', tourCode: '', label: '', migratedFrom: '', source: { ...EMPTY_SOURCE } });
export const newRateRule = () => ({ airline: '', origin: '', destination: '', kind: '', value: '', source: { ...EMPTY_SOURCE } });
export const newDirectBooking = () => ({ airline: '', method: '', detail: '', source: { ...EMPTY_SOURCE } });
export const newPolicyLink = () => ({ title: '', url: '', source: { ...EMPTY_SOURCE } });

export function toForm(channel) {
    const c = channel || {};
    const por = c.platformOfRecord || {};
    const tmc = c.tmc || {};
    const fees = tmc.feesUSD || {};
    const tr = c.travelRequest || {};
    const pf = c.privateFares || {};
    return {
        mode: s(c.mode) || 'united_books',
        platformOfRecord: {
            kind: s(por.kind), name: s(por.name), url: s(por.url), companyUuid: s(por.companyUuid),
            deepLinkSupported: por.deepLinkSupported === true ? 'yes' : por.deepLinkSupported === false ? 'no' : 'unknown',
            source: sourceToForm(por.source),
        },
        tmc: {
            name: s(tmc.name), agentEmail: s(tmc.agentEmail), agentPhone: s(tmc.agentPhone), hours: s(tmc.hours),
            feesUSD: { online: s(fees.online), agentDomestic: s(fees.agentDomestic), agentInternational: s(fees.agentInternational) },
            source: sourceToForm(tmc.source),
        },
        preferredChannelOrder: Array.isArray(c.preferredChannelOrder) ? c.preferredChannelOrder.slice() : [],
        travelRequest: { required: s(tr.required) || 'none', system: s(tr.system), formName: s(tr.formName), url: s(tr.url), source: sourceToForm(tr.source) },
        privateFares: Object.entries(pf).flatMap(([airline, list]) => (Array.isArray(list) ? list : []).map((e) => ({
            airline, id: s(e.id), corporateCode: s(e.corporateCode), tourCode: s(e.tourCode), label: s(e.label), migratedFrom: s(e.migratedFrom), source: sourceToForm(e.source),
        }))),
        rateRules: (c.rateRules || []).map((r) => ({
            airline: s(r.airline), origin: s(r.origin), destination: s(r.destination),
            kind: s(r.rule && r.rule.kind), value: s(r.rule && r.rule.value), source: sourceToForm(r.source),
        })),
        directBooking: (c.directBooking || []).map((d) => ({ airline: s(d.airline), method: s(d.method), detail: s(d.detail), source: sourceToForm(d.source) })),
        perDiem: { source: s(c.perDiem && c.perDiem.source), url: s(c.perDiem && c.perDiem.url) },
        policyLinks: (c.policyLinks || []).map((p) => ({ title: s(p.title), url: s(p.url), source: sourceToForm(p.source) })),
        payment: s(c.payment),
    };
}

export function fromForm(f) {
    const pf = {};
    for (const row of f.privateFares || []) {
        const code = upper(row.airline);
        if (!code) continue;
        (pf[code] = pf[code] || []).push({
            ...(t(row.id) ? { id: t(row.id) } : {}),
            corporateCode: t(row.corporateCode), tourCode: t(row.tourCode),
            source: sourceFromForm(row.source), migratedFrom: t(row.migratedFrom), label: t(row.label),
        });
    }
    // An entry with no stored id gets one from org-backend (privateFareId); the key order
    // matches normalize so a stored entry comes back equal.
    for (const list of Object.values(pf)) for (const e of list) if (!e.id) delete e.id;
    const perDiemSource = t(f.perDiem && f.perDiem.source);
    const perDiemUrl = t(f.perDiem && f.perDiem.url);
    const dl = f.platformOfRecord.deepLinkSupported;
    return {
        mode: t(f.mode) || 'united_books',
        platformOfRecord: {
            kind: t(f.platformOfRecord.kind), name: t(f.platformOfRecord.name), url: t(f.platformOfRecord.url), companyUuid: t(f.platformOfRecord.companyUuid),
            deepLinkSupported: dl === 'yes' ? true : dl === 'no' ? false : null,
            source: sourceFromForm(f.platformOfRecord.source),
        },
        tmc: {
            name: t(f.tmc.name), agentEmail: t(f.tmc.agentEmail), agentPhone: t(f.tmc.agentPhone), hours: t(f.tmc.hours),
            feesUSD: { online: numFromForm(f.tmc.feesUSD.online), agentDomestic: numFromForm(f.tmc.feesUSD.agentDomestic), agentInternational: numFromForm(f.tmc.feesUSD.agentInternational) },
            source: sourceFromForm(f.tmc.source),
        },
        preferredChannelOrder: (f.preferredChannelOrder || []).slice(),
        travelRequest: {
            required: t(f.travelRequest.required) || 'none', system: t(f.travelRequest.system), formName: t(f.travelRequest.formName), url: t(f.travelRequest.url),
            source: sourceFromForm(f.travelRequest.source),
        },
        privateFares: pf,
        rateRules: (f.rateRules || []).map((r) => ({
            airline: upper(r.airline), origin: upper(r.origin), destination: upper(r.destination),
            rule: { kind: t(r.kind), value: numFromForm(r.value) },
            source: sourceFromForm(r.source),
        })),
        directBooking: (f.directBooking || []).map((d) => ({ airline: t(d.airline), method: t(d.method), detail: t(d.detail), source: sourceFromForm(d.source) })),
        perDiem: perDiemSource || perDiemUrl ? { source: perDiemSource, url: perDiemUrl } : null,
        policyLinks: (f.policyLinks || []).map((p) => ({ title: t(p.title), url: t(p.url), source: sourceFromForm(p.source) })),
        payment: t(f.payment),
    };
}

/** The contract's path for contract airline row i: its index among that airline's rows. */
export function privateFarePath(rows, i) {
    const code = upper(rows[i] && rows[i].airline) || '';
    let k = 0;
    for (let j = 0; j < i; j++) if (upper(rows[j].airline) === code) k++;
    return 'privateFares.' + code + '[' + k + ']';
}

// The contract's Rule 1b list (travelChannelContract.js validate): which outside facts need a source.
export function missingSources(f) {
    const out = [];
    const need = (path, label, src) => { if (!sourceComplete(src)) out.push({ path, label }); };
    const tmcFacts = [f.tmc.name, f.tmc.agentEmail, f.tmc.agentPhone, f.tmc.hours, f.tmc.feesUSD.online, f.tmc.feesUSD.agentDomestic, f.tmc.feesUSD.agentInternational].some((v) => t(s(v)));
    if (tmcFacts) need('tmc.source', 'Travel agency', f.tmc.source);
    if (t(f.platformOfRecord.name) || t(f.platformOfRecord.url)) need('platformOfRecord.source', 'Platform of record', f.platformOfRecord.source);
    if (t(f.travelRequest.url)) need('travelRequest.source', 'Travel request url', f.travelRequest.source);
    (f.privateFares || []).forEach((r, i) => need(privateFarePath(f.privateFares, i) + '.source', 'Contract airline row ' + (i + 1), r.source));
    (f.rateRules || []).forEach((r, i) => need('rateRules[' + i + '].source', 'Rate rule ' + (i + 1), r.source));
    (f.directBooking || []).forEach((r, i) => need('directBooking[' + i + '].source', 'Direct booking rule ' + (i + 1), r.source));
    (f.policyLinks || []).forEach((r, i) => need('policyLinks[' + i + '].source', 'Policy link ' + (i + 1), r.source));
    return out;
}

const humanList = (xs) => (xs || []).join(', ');

/** One readable line for an org-backend refusal. Unknown codes are shown as they came. */
export function refusalLine(err) {
    const e = err || {};
    const d = e.detail || {};
    switch (e.code) {
        case 'TRAVEL_CHANNEL_SOURCE_MISSING': return 'Needs its source: the page this came from and the date it was read.';
        case 'TRAVEL_CHANNEL_MODE_INVALID': return 'The mode must be United books or Platform of record.';
        case 'TRAVEL_CHANNEL_ADAPTER_UNKNOWN': return '"' + (d.supplied || '') + '" is not an adapter org-backend has. It has ' + humanList(d.allowed) + '.';
        case 'TRAVEL_CHANNEL_ADAPTER_ORDER': return 'Agent phone has to be last in the order.';
        case 'TRAVEL_CHANNEL_PRIVATE_FARE_AIRLINE': return 'Duffel takes contract codes only for ' + humanList(d.allowed) + '.';
        case 'TRAVEL_CHANNEL_PRIVATE_FARE_FIELD': return d.rule ? 'Contract code: ' + d.rule + '.' : 'This airline needs ' + humanList(d.requires) + '.';
        case 'TRAVEL_CHANNEL_RATE_RULE_INVALID': return 'Rate rule: ' + (d.rule || 'not valid') + '.';
        case 'TRAVEL_CHANNEL_WALLET_IN_PLATFORM_MODE': return 'A platform of record customer never pays from the wallet.';
        case 'ORG_NOT_FOUND': return 'org-backend has no organisation with this id.';
        default: return (e.code || 'Refused') + (e.message ? ': ' + e.message : '');
    }
}
